import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectGvc, setGvc } from "../store/contextSlice";

const URL_PARAM_GVC = "gvc";

interface UseGvcListUrlStateOptions {
  localStorageKey: string;
}

/**
 * Hook to sync the "Limit with Context GVC" checkbox state with URL search params.
 * Enables shareable links like `/console/org/my-org/workload/?gvc=my-gvc`.
 *
 * On mount: reads `?gvc=X` from URL → sets global GVC context + enables filter.
 * On change: syncs filter state + GVC back to URL search params.
 * Falls back to localStorage when no URL param is present.
 */
export function useGvcListUrlState({ localStorageKey }: UseGvcListUrlStateOptions) {
  const dispatch = useAppDispatch();
  const gvc = useAppSelector(selectGvc);
  const [searchParams, setSearchParams] = useSearchParams();

  const initializedRef = useRef(false);
  const urlReadCompleteRef = useRef(false);

  // Initialize from URL first, then localStorage fallback
  const [limitToContextGVC, setLimitToContextGVC] = useState(() => {
    const urlGvc = new URLSearchParams(window.location.search).get(URL_PARAM_GVC);
    if (urlGvc) {
      return true;
    }
    const localValue = localStorage.getItem(localStorageKey);
    if (!localValue) {
      return true; // default
    }
    return localValue === "true";
  });

  // On mount: if URL has ?gvc=X, dispatch setGvc to Redux (once)
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const urlGvc = searchParams.get(URL_PARAM_GVC);
    if (urlGvc) {
      dispatch(setGvc(urlGvc));
    }

    // Allow React state to settle before enabling state → URL sync
    setTimeout(() => {
      urlReadCompleteRef.current = true;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to localStorage (secondary persistence for non-URL navigation)
  useEffect(() => {
    localStorage.setItem(localStorageKey, String(limitToContextGVC));
  }, [limitToContextGVC, localStorageKey]);

  // Sync state → URL (after initialization complete)
  useEffect(() => {
    if (!urlReadCompleteRef.current) return;

    const newParams = new URLSearchParams(searchParams);

    if (limitToContextGVC && gvc) {
      newParams.set(URL_PARAM_GVC, gvc);
    } else {
      newParams.delete(URL_PARAM_GVC);
    }

    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitToContextGVC, gvc]);

  return {
    limitToContextGVC,
    setLimitToContextGVC,
  };
}
